import { useState, useEffect } from 'react';
import {
  getUsers,
  getInsuranceCompanies,
  getReferralSources,
  getDamageCategories,
} from '../api/leads-api';
import { getSubcontractorLookup } from '@/features/subcontractors/api/subcontractors';
import type {
  UserReference,
  InsuranceCompanyReference,
  ReferralSourceReference,
  DamageCategoryReference,
} from '../types';
import type { SubcontractorLookupDto } from '@/features/subcontractors/types';

/**
 * Hook for fetching all reference data needed for lead forms.
 * Uses Promise.allSettled to ensure partial failures don't prevent other data from loading.
 */
export function useReferenceData() {
  const [users, setUsers] = useState<UserReference[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<InsuranceCompanyReference[]>([]);
  const [referralSources, setReferralSources] = useState<ReferralSourceReference[]>([]);
  const [damageCategories, setDamageCategories] = useState<DamageCategoryReference[]>([]);
  const [subcontractors, setSubcontractors] = useState<SubcontractorLookupDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      const [usersResult, companiesResult, sourcesResult, damageCategoriesResult, subcontractorsResult] = await Promise.allSettled([
        getUsers(),
        getInsuranceCompanies(),
        getReferralSources(),
        getDamageCategories(),
        getSubcontractorLookup(),
      ]);

      const sortByName = <T extends { name: string }>(items: T[]): T[] =>
        [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

      if (usersResult.status === 'fulfilled') {
        setUsers(sortByName(usersResult.value));
      }
      if (companiesResult.status === 'fulfilled') {
        setInsuranceCompanies(sortByName(companiesResult.value));
      }
      if (sourcesResult.status === 'fulfilled') {
        setReferralSources(sourcesResult.value); // Uses admin-configured SortOrder
      }
      if (damageCategoriesResult.status === 'fulfilled') {
        setDamageCategories(damageCategoriesResult.value); // Uses admin-configured SortOrder
      }
      if (subcontractorsResult.status === 'fulfilled') {
        setSubcontractors(
          [...subcontractorsResult.value].sort((a, b) =>
            a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' })
          )
        );
      }

      // Set error if any critical fetch failed
      const failures = [usersResult, companiesResult, sourcesResult, damageCategoriesResult].filter(
        (r) => r.status === 'rejected'
      );
      if (failures.length > 0) {
        setError('Some reference data failed to load');
      }

      setIsLoading(false);
    };

    fetchData();
  }, []);

  return {
    users,
    insuranceCompanies,
    referralSources,
    damageCategories,
    subcontractors,
    isLoading,
    error,
  };
}
